import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { memberApi } from '../services/memberApi';

// Helper function to get full image URL (handles both Cloudinary and local URLs)
const getImageUrl = (url, baseUrl) => {
  if (!url) return null;
  // If it's already a full URL (Cloudinary), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Otherwise, prepend the base URL (for legacy local paths)
  return `${baseUrl}/uploads/${url}`;
};

// Accepts a directory member in camelCase OR lowercase database naming
// and always returns the camelCase names this page uses.
const normalizeMember = (m) => {
  if (!m) return m;
  return {
    ...m,
    membershipNumber: m.membershipNumber ?? m.membershipnumber ?? '',
    firstName: m.firstName ?? m.firstname ?? '',
    middleName: m.middleName ?? m.middlename ?? '',
    lastName: m.lastName ?? m.lastname ?? '',
    areaOfSpecialty: m.areaOfSpecialty ?? m.areaofspecialty ?? '',
    currentPractice: m.currentPractice ?? m.currentpractice ?? '',
    profilePhoto: m.profilePhoto ?? m.profilephoto ?? null,
    joinedDate: m.joinedDate ?? m.joineddate ?? null,
    fellowshipCollege: m.fellowshipCollege ?? m.fellowshipcollege ?? '',
    fwacs: m.fwacs || false,
    fmcs: m.fmcs || false,
    facs: m.facs || false,
    frcs: m.frcs || false
  };
};

// "First M. Last" for cards
const getShortName = (m) => {
  const initial = m.middleName ? `${m.middleName.charAt(0)}.` : '';
  return [m.firstName, initial, m.lastName].filter(Boolean).join(' ').trim();
};

// "First Middle Last" for the detail modal
const getFullName = (m) =>
  [m.firstName, m.middleName, m.lastName].filter(Boolean).join(' ').trim();

const MemberDashboard = () => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [updatingPrivacy, setUpdatingPrivacy] = useState(false);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const navigate = useNavigate();

  // Directory state
  const [directoryMembers, setDirectoryMembers] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });
  const [selectedMember, setSelectedMember] = useState(null);

  const API_BASE_URL = 'https://naus-backend.onrender.com';

  // Load member data from API
  useEffect(() => {
    fetchMemberProfile();
  }, []);

  // Disable body scroll when photo viewer or member modal is open
  useEffect(() => {
    if (showPhotoViewer || selectedMember) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPhotoViewer, selectedMember]);

  const fetchMemberProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await memberApi.getProfile();
      setMemberData(response.data);
      setEditedData(response.data);
    } catch (err) {
      console.error('Error loading member profile:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('member');
        navigate('/member-login');
      } else {
        setError('Failed to load profile data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('member');
    navigate('/member-login');
  };

  // Handle edit mode toggle
  const toggleEditMode = () => {
    if (isEditing) {
      // Cancel editing, revert changes
      setEditedData(memberData);
    }
    setIsEditing(!isEditing);
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value
    });
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await memberApi.updateProfile({
        firstName: editedData.firstName,
        middleName: editedData.middleName,
        lastName: editedData.lastName,
        phoneNumber: editedData.phoneNumber,
        streetAddress: editedData.streetAddress,
        permanentAddress: editedData.permanentAddress,
        currentPractice: editedData.currentPractice,
        nextOfKinName: editedData.nextOfKinName,
        nextOfKinPhone: editedData.nextOfKinPhone,
        nextOfKinEmail: editedData.nextOfKinEmail
      });

      // Refresh profile data
      await fetchMemberProfile();
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (err) {
      console.error('Error updating profile:', err);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const currentPassword = formData.get('currentPassword');
    const newPassword = formData.get('newPassword');
    const confirmNewPassword = formData.get('confirmNewPassword');

    if (newPassword !== confirmNewPassword) {
      alert('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      await memberApi.changePassword({
        currentPassword,
        newPassword
      });

      alert('Password changed successfully!');
      setShowPasswordModal(false);
      e.target.reset();
    } catch (err) {
      console.error('Password change error:', err);
      alert(err.response?.data?.message || 'Failed to change password');
    }
  };

  // Handle certificate upload
  const handleCertificateUpload = async (e, certificateType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF, JPG, JPEG, and PNG files are allowed');
      return;
    }

    try {
      setUploadingCert(certificateType);

      const formData = new FormData();
      formData.append('certificate', file);
      formData.append('certificateType', certificateType);

      await memberApi.uploadCertificate(formData);

      // Refresh profile to get updated certificate info
      await fetchMemberProfile();
      alert('Certificate uploaded successfully!');
    } catch (err) {
      console.error('Certificate upload error:', err);
      alert(err.response?.data?.message || 'Failed to upload certificate');
    } finally {
      setUploadingCert(null);
      e.target.value = ''; // Reset file input
    }
  };

  // Download certificate
  const downloadCertificate = async (certificateType) => {
    try {
      const response = await memberApi.downloadCertificate(certificateType);

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${certificateType}-certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Certificate download error:', err);
      alert(err.response?.data?.message || 'Certificate not available');
    }
  };

  // Handle profile photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Photo size must be less than 2MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPG, JPEG, and PNG images are allowed');
      return;
    }

    try {
      setUploadingPhoto(true);

      const formData = new FormData();
      formData.append('photo', file);

      await memberApi.uploadProfilePhoto(formData);

      // Refresh profile to get updated photo
      await fetchMemberProfile();
      alert('Profile photo uploaded successfully!');
    } catch (err) {
      console.error('Photo upload error:', err);
      alert(err.response?.data?.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Handle privacy toggle
  const handlePrivacyToggle = async () => {
    try {
      setUpdatingPrivacy(true);
      // Handle null/undefined as false (default to not showing)
      const currentValue = memberData.showInDirectory === true || memberData.showInDirectory === 1;
      const newValue = !currentValue;
      await memberApi.updatePrivacy(newValue);
      setMemberData({ ...memberData, showInDirectory: newValue });
      alert(`You are now ${newValue ? 'visible' : 'hidden'} in the member directory`);
    } catch (err) {
      console.error('Privacy update error:', err);
      alert(err.response?.data?.message || 'Failed to update privacy settings');
    } finally {
      setUpdatingPrivacy(false);
    }
  };

  // Fetch directory members
  const fetchDirectoryMembers = async () => {
    try {
      setDirectoryLoading(true);
      setDirectoryError('');

      const response = await memberApi.getDirectory({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        specialty: specialty || undefined
      });

      const list = response.data?.members || [];
      const serverPagination = response.data?.pagination || {};

      // Normalize so the cards work with either naming style from the server
      setDirectoryMembers(list.map(normalizeMember));
      setPagination(prev => ({
        ...prev,
        total: serverPagination.total ?? list.length,
        totalPages: serverPagination.totalPages ?? 1
      }));
    } catch (err) {
      console.error('Error fetching directory:', err);
      setDirectoryError('Failed to load member directory');
    } finally {
      setDirectoryLoading(false);
    }
  };

  // Handle directory search
  const handleDirectorySearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchDirectoryMembers();
  };

  // Handle directory page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // View member profile in directory
  const viewMemberProfile = async (membershipNumber) => {
    // Never request /directory/undefined
    if (!membershipNumber) {
      alert('This member does not have a membership number yet.');
      return;
    }

    try {
      const response = await memberApi.getDirectoryMember(membershipNumber);
      setSelectedMember(normalizeMember(response.data));
    } catch (err) {
      console.error('Error fetching member profile:', err);
      alert('Could not load member profile');
    }
  };

  // Close member modal
  const closeMemberModal = () => {
    setSelectedMember(null);
  };

  // Fetch directory when tab changes or pagination/filters change
  useEffect(() => {
    if (activeTab === 'directory') {
      fetchDirectoryMembers();
    }
  }, [activeTab, pagination.page]);

  // Generate QR code URL (using a free QR code API)
  const getQRCodeUrl = () => {
    const verificationUrl = `https://nausurology.org/verify/${memberData?.membershipNumber}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(verificationUrl)}`;
  };

  if (loading) {
    return (
      <div className="section">
        <div className="container">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            backgroundColor: 'var(--white)',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: 'var(--shadow)'
          }}>
            <div>Loading member data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section">
        <div className="container">
          <div style={{
            backgroundColor: 'var(--white)',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: 'var(--shadow)',
            textAlign: 'center'
          }}>
            <h3 style={{ color: '#dc3545', marginBottom: '1rem' }}>Error Loading Data</h3>
            <p>{error}</p>
            <button
              onClick={fetchMemberProfile}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--primary-green)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                marginTop: '1rem',
                marginRight: '1rem'
              }}
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/member-login')}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <div className="container">
        {/* Header with logout */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 className="section-title">Member Dashboard</h1>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>
              Welcome back, {memberData?.firstName} {memberData?.lastName}
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            Logout
          </button>
        </div>

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          borderBottom: '2px solid #dee2e6',
          overflowX: 'auto'
        }}>
          {[
            { id: 'profile', label: 'Profile' },
            { id: 'card', label: 'Membership Card' },
            { id: 'certificates', label: 'Certificates' },
            { id: 'directory', label: 'Member Directory' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: activeTab === tab.id ? 'var(--primary-green)' : '#f8f9fa',
                color: activeTab === tab.id ? 'white' : '#495057',
                border: 'none',
                borderRadius: '6px 6px 0 0',
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div style={{
            backgroundColor: 'var(--white)',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: 'var(--shadow)'
          }}>
            {/* Profile Photo Section */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '2rem',
              paddingBottom: '2rem',
              borderBottom: '1px solid #eee'
            }}>
              <div style={{
                position: 'relative',
                marginBottom: '1rem'
              }}>
                <div
                  onClick={() => memberData?.profilePhoto && setShowPhotoViewer(true)}
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '4px solid var(--primary-green)',
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: memberData?.profilePhoto ? 'pointer' : 'default',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (memberData?.profilePhoto) {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 100, 0, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  title={memberData?.profilePhoto ? 'Click to view full photo' : ''}
                >
                  {memberData?.profilePhoto ? (
                    <img
                      src={getImageUrl(memberData.profilePhoto, API_BASE_URL)}
                      alt="Profile"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <span style={{
                    fontSize: '3rem',
                    color: '#ccc',
                    display: memberData?.profilePhoto ? 'none' : 'flex',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>👤</span>
                </div>
                <label style={{
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--primary-green)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                  border: '2px solid white',
                  fontSize: '1.2rem'
                }}>
                  {uploadingPhoto ? '...' : '📷'}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handlePhotoUpload}
                    disabled={uploadingPhoto}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
              <h2 style={{ margin: '0', color: 'var(--primary-green)' }}>
                {memberData?.firstName} {memberData?.lastName}
              </h2>
              <p style={{ margin: '0.25rem 0', color: '#666' }}>{memberData?.membershipNumber}</p>
              <span style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                fontSize: '0.85rem',
                fontWeight: '500',
                backgroundColor: memberData?.isActive ? '#d4edda' : '#f8d7da',
                color: memberData?.isActive ? '#155724' : '#721c24',
                marginTop: '0.5rem'
              }}>
                {memberData?.isActive ? 'Active Member' : 'Inactive'}
              </span>

              {/* Privacy Toggle */}
              <div style={{
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <span style={{ fontSize: '0.9rem', color: '#495057' }}>Show in Member Directory:</span>
                <button
                  onClick={handlePrivacyToggle}
                  disabled={updatingPrivacy}
                  style={{
                    width: '50px',
                    height: '26px',
                    borderRadius: '13px',
                    border: 'none',
                    backgroundColor: (memberData?.showInDirectory === true || memberData?.showInDirectory === 1) ? '#28a745' : '#ccc',
                    cursor: updatingPrivacy ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    transition: 'background-color 0.3s'
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    top: '3px',
                    left: (memberData?.showInDirectory === true || memberData?.showInDirectory === 1) ? '27px' : '3px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    transition: 'left 0.3s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </button>
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <h3 style={{ color: 'var(--primary-green)' }}>Profile Information</h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Change Password
                </button>
                {!isEditing ? (
                  <button
                    onClick={toggleEditMode}
                    style={{
                      backgroundColor: 'var(--primary-green)',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Edit Profile
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        opacity: saving ? 0.6 : 1
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={toggleEditMode}
                      disabled={saving}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {/* Personal Information */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: '#495057' }}>Personal Information</h4>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <strong>First Name:</strong>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.firstName || ''}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.firstName}</div>
                    )}
                  </div>

                  <div>
                    <strong>Middle Name:</strong>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.middleName || ''}
                        onChange={(e) => handleInputChange('middleName', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.middleName || 'N/A'}</div>
                    )}
                  </div>

                  <div>
                    <strong>Last Name:</strong>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.lastName || ''}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.lastName}</div>
                    )}
                  </div>

                  <div>
                    <strong>Email:</strong>
                    <div>{memberData?.email}</div>
                  </div>

                  <div>
                    <strong>Phone:</strong>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editedData.phoneNumber || ''}
                        onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.phoneNumber}</div>
                    )}
                  </div>

                  <div>
                    <strong>Specialty:</strong>
                    <div>{memberData?.areaOfSpecialty}</div>
                  </div>

                  <div>
                    <strong>MDCN Registration:</strong>
                    <div>{memberData?.mdcnRegistrationNumber}</div>
                  </div>
                </div>
              </div>

              {/* Membership & Address */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: '#495057' }}>Membership Details</h4>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <strong>Membership Number:</strong>
                    <div>{memberData?.membershipNumber}</div>
                  </div>

                  <div>
                    <strong>Membership Type:</strong>
                    <div>{memberData?.membershipType}</div>
                  </div>

                  <div>
                    <strong>Join Date:</strong>
                    <div>{memberData?.joinedDate ? new Date(memberData.joinedDate).toLocaleDateString() : 'N/A'}</div>
                  </div>

                  <div>
                    <strong>Status:</strong>
                    <span style={{
                      display: 'inline-block',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      backgroundColor: memberData?.isActive ? '#d4edda' : '#f8d7da',
                      color: memberData?.isActive ? '#155724' : '#721c24'
                    }}>
                      {memberData?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div>
                    <strong>Street Address:</strong>
                    {isEditing ? (
                      <textarea
                        value={editedData.streetAddress || ''}
                        onChange={(e) => handleInputChange('streetAddress', e.target.value)}
                        rows="2"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.streetAddress}</div>
                    )}
                  </div>

                  <div>
                    <strong>Permanent Address:</strong>
                    {isEditing ? (
                      <textarea
                        value={editedData.permanentAddress || ''}
                        onChange={(e) => handleInputChange('permanentAddress', e.target.value)}
                        rows="2"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.permanentAddress}</div>
                    )}
                  </div>

                  <div>
                    <strong>Current Practice:</strong>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.currentPractice || ''}
                        onChange={(e) => handleInputChange('currentPractice', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.currentPractice}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Next of Kin */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: '#495057' }}>Next of Kin</h4>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <strong>Name:</strong>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.nextOfKinName || ''}
                        onChange={(e) => handleInputChange('nextOfKinName', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.nextOfKinName}</div>
                    )}
                  </div>

                  <div>
                    <strong>Phone:</strong>
                    {isEditing ? (
                      <input
                        type="tel"
                        value={editedData.nextOfKinPhone || ''}
                        onChange={(e) => handleInputChange('nextOfKinPhone', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.nextOfKinPhone}</div>
                    )}
                  </div>

                  <div>
                    <strong>Email:</strong>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editedData.nextOfKinEmail || ''}
                        onChange={(e) => handleInputChange('nextOfKinEmail', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          marginTop: '0.25rem'
                        }}
                      />
                    ) : (
                      <div>{memberData?.nextOfKinEmail}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Membership Card Tab */}
        {activeTab === 'card' && (
          <div style={{
            backgroundColor: 'var(--white)',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: 'var(--shadow)'
          }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '2rem', textAlign: 'center' }}>
              Digital Membership Card
            </h3>

            {/* Membership Card */}
            <div id="membership-card" style={{
              maxWidth: '400px',
              margin: '0 auto 2rem',
              background: 'linear-gradient(135deg, #003d00 0%, #005500 50%, #006400 100%)',
              borderRadius: '16px',
              padding: '24px',
              color: '#ffffff',
              boxShadow: '0 10px 40px rgba(0, 100, 0, 0.4)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Background Pattern */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.05,
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
              }} />

              {/* Card Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '20px',
                position: 'relative',
                zIndex: 1
              }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '0.85rem', letterSpacing: '1px', color: '#ffffff', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                    ASSOCIATION OF UROLOGICAL SURGEONS, 
                  </h4>
                  <h3 style={{ margin: '4px 0 0', fontSize: '1.1rem', fontWeight: 'bold', color: '#ffffff', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                    NIGERIA
                  </h3>
                </div>
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.6rem',
                  color: '#003d00',
                  fontWeight: 'bold'
                }}>
                  NAUS
                </div>
              </div>

              {/* Member Photo and Info */}
              <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '20px',
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{
                  width: '80px',
                  height: '100px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                  border: '3px solid #ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {memberData?.profilePhoto ? (
                    <img
                      src={getImageUrl(memberData.profilePhoto, API_BASE_URL)}
                      alt="Profile"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : null}
                  <span style={{ fontSize: '2.5rem', color: '#666', display: memberData?.profilePhoto ? 'none' : 'block' }}>👤</span>
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: '0 0 4px', fontSize: '1.2rem', color: '#ffffff', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
                    {memberData?.firstName} {memberData?.middleName ? memberData.middleName.charAt(0) + '.' : ''} {memberData?.lastName}
                  </h2>
                  <p style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#c8e6c9', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                    {memberData?.areaOfSpecialty || 'Urological Surgeon'}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#a5d6a7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                    {memberData?.currentPractice}
                  </p>
                </div>
              </div>

              {/* Member Details */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                position: 'relative',
                zIndex: 1
              }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '0.75rem', color: '#c8e6c9', fontWeight: '600', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>MEMBERSHIP NO.</p>
                  <p style={{ margin: '0 0 12px', fontSize: '1.1rem', fontWeight: 'bold', letterSpacing: '1px', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
                    {memberData?.membershipNumber}
                  </p>
                  <p style={{ margin: '0 0 4px', fontSize: '0.75rem', color: '#c8e6c9', fontWeight: '600', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>MEMBER SINCE</p>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#ffffff', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                    {memberData?.joinedDate ? new Date(memberData.joinedDate).getFullYear() : 'N/A'}
                  </p>
                </div>
                {/* QR Code */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '6px',
                  borderRadius: '8px'
                }}>
                  <img
                    src={getQRCodeUrl()}
                    alt="Verification QR Code"
                    style={{ width: '70px', height: '70px', display: 'block' }}
                  />
                </div>
              </div>
            </div>

            {/* Download/Print Instructions */}
            <div style={{
              textAlign: 'center',
              padding: '1.5rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginTop: '1rem'
            }}>
              <p style={{ margin: '0 0 1rem', color: '#666' }}>
                To save your membership card, take a screenshot or use your browser's print function (Ctrl+P / Cmd+P).
              </p>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#999' }}>
                The QR code links to your verification page on the NAUS website.
              </p>
            </div>
          </div>
        )}

        {/* Certificates Tab */}
        {activeTab === 'certificates' && (
          <div style={{
            backgroundColor: 'var(--white)',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: 'var(--shadow)'
          }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '2rem' }}>Manage Certificates</h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '2rem' }}>
              {/* MBBS Certificate */}
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎓</div>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                  MBBS Certificate
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#6c757d',
                  marginBottom: '1rem'
                }}>
                  {memberData?.mbbsCertificate ? (
                    <span style={{ color: '#28a745' }}>✓ Uploaded</span>
                  ) : (
                    <span style={{ color: '#dc3545' }}>Not uploaded</span>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {memberData?.mbbsCertificate && (
                    <button
                      onClick={() => downloadCertificate('mbbs')}
                      style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: '#006400',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      📥 Download
                    </button>
                  )}

                  <label style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: memberData?.mbbsCertificate ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: uploadingCert === 'mbbs' ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'block',
                    opacity: uploadingCert === 'mbbs' ? 0.6 : 1
                  }}>
                    {uploadingCert === 'mbbs' ? 'Uploading...' : (memberData?.mbbsCertificate ? '🔄 Replace' : '📤 Upload')}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleCertificateUpload(e, 'mbbs')}
                      disabled={uploadingCert === 'mbbs'}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem' }}>
                  PDF, JPG, PNG (Max 5MB)
                </div>
              </div>

              {/* Fellowship Certificate */}
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📜</div>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                  Fellowship Certificate
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#6c757d',
                  marginBottom: '1rem'
                }}>
                  {memberData?.fellowshipCertificate ? (
                    <span style={{ color: '#28a745' }}>✓ Uploaded</span>
                  ) : (
                    <span style={{ color: '#dc3545' }}>Not uploaded</span>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {memberData?.fellowshipCertificate && (
                    <button
                      onClick={() => downloadCertificate('fellowship')}
                      style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: '#006400',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      📥 Download
                    </button>
                  )}

                  <label style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: memberData?.fellowshipCertificate ? '#6c757d' : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: uploadingCert === 'fellowship' ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'block',
                    opacity: uploadingCert === 'fellowship' ? 0.6 : 1
                  }}>
                    {uploadingCert === 'fellowship' ? 'Uploading...' : (memberData?.fellowshipCertificate ? '🔄 Replace' : '📤 Upload')}
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleCertificateUpload(e, 'fellowship')}
                      disabled={uploadingCert === 'fellowship'}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem' }}>
                  PDF, JPG, PNG (Max 5MB)
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Directory Tab */}
        {activeTab === 'directory' && (
          <div style={{
            backgroundColor: 'var(--white)',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: 'var(--shadow)'
          }}>
            <h2 style={{ color: 'var(--primary-green)', marginBottom: '0.75rem', fontSize: '1.5rem', textAlign: 'center' }}>Member Directory</h2>
            <p style={{ color: '#666', marginBottom: '1.25rem', fontSize: '0.95rem', textAlign: 'center' }}>
              Connect with fellow urological surgeons across Nigeria
            </p>

            {/* Search and Filter */}
            <form onSubmit={handleDirectorySearch} style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              justifyContent: 'center',
              marginBottom: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#495057', fontSize: '0.9rem' }}>
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Name or institution..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', color: '#495057', fontSize: '0.9rem' }}>
                  Specialty
                </label>
                <select
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    boxSizing: 'border-box',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">All Specialties</option>
                  <option value="General Urology">General Urology</option>
                  <option value="Pediatric Urology">Pediatric Urology</option>
                  <option value="Urologic Oncology">Urologic Oncology</option>
                  <option value="Female Urology">Female Urology</option>
                  <option value="Endourology">Endourology</option>
                  <option value="Reconstructive Urology">Reconstructive Urology</option>
                  <option value="Andrology">Andrology</option>
                </select>
              </div>
              <button
                type="submit"
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--primary-green)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}
              >
                Search
              </button>
            </form>

            {/* Results Count */}
            {!directoryLoading && !directoryError && (
              <p style={{ color: '#666', marginBottom: '1rem' }}>
                Showing {directoryMembers.length} of {pagination.total} members
              </p>
            )}

            {/* Loading State */}
            {directoryLoading && (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                Loading members...
              </div>
            )}

            {/* Error State */}
            {directoryError && (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '8px',
                marginBottom: '2rem'
              }}>
                {directoryError}
                <button
                  onClick={fetchDirectoryMembers}
                  style={{
                    display: 'block',
                    margin: '1rem auto 0',
                    padding: '0.5rem 1rem',
                    backgroundColor: '#721c24',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Members Grid */}
            {!directoryLoading && !directoryError && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '1.25rem',
                marginBottom: '1.5rem',
                justifyContent: 'center',
                marginTop: '0.5rem'
              }}>
                {directoryMembers.map((member, index) => (
                  <div
                    key={`${member.membershipNumber || 'member'}-${index}`}
                    onClick={() => viewMemberProfile(member.membershipNumber)}
                    style={{
                      backgroundColor: '#f8f9fa',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      border: '1px solid #eee'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {/* Member Photo */}
                    <div style={{
                      height: '150px',
                      backgroundColor: '#e9ecef',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderBottom: '3px solid var(--primary-green)'
                    }}>
                      {member.profilePhoto ? (
                        <img
                          src={getImageUrl(member.profilePhoto, API_BASE_URL)}
                          alt={getShortName(member) || 'Member'}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <span style={{
                        fontSize: '4rem',
                        color: '#ccc',
                        display: member.profilePhoto ? 'none' : 'flex',
                        width: '100%',
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        👤
                      </span>
                    </div>

                    {/* Member Info */}
                    <div style={{ padding: '1rem' }}>
                      <h3 style={{
                        margin: '0 0 0.5rem',
                        color: 'var(--primary-green)',
                        fontSize: '1rem'
                      }}>
                        {getShortName(member) || member.membershipNumber || 'Unnamed member'}
                      </h3>
                      {member.areaOfSpecialty && (
                        <p style={{ margin: '0 0 0.25rem', color: '#666', fontSize: '0.85rem' }}>
                          {member.areaOfSpecialty}
                        </p>
                      )}
                      {member.currentPractice && (
                        <p style={{ margin: 0, color: '#999', fontSize: '0.8rem' }}>
                          {member.currentPractice}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!directoryLoading && !directoryError && directoryMembers.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                color: '#666'
              }}>
                <p style={{ fontSize: '3rem', margin: '0 0 1rem' }}>🔍</p>
                <p>No members found matching your criteria.</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSpecialty('');
                    setPagination(prev => ({ ...prev, page: 1 }));
                    fetchDirectoryMembers();
                  }}
                  style={{
                    marginTop: '1rem',
                    padding: '0.5rem 1rem',
                    backgroundColor: 'var(--primary-green)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}

            {/* Pagination */}
            {!directoryLoading && !directoryError && pagination.totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: pagination.page === 1 ? '#ccc' : 'var(--primary-green)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: pagination.page === pageNum ? 'var(--primary-green)' : '#f0f0f0',
                        color: pagination.page === pageNum ? 'white' : '#333',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: pagination.page === pageNum ? 'bold' : 'normal'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: pagination.page === pagination.totalPages ? '#ccc' : 'var(--primary-green)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Member Detail Modal - Using Portal for proper centering */}
        {selectedMember && ReactDOM.createPortal(
          <div
            onClick={closeMemberModal}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              zIndex: 999999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: window.innerWidth <= 480 ? '10px' : '20px',
              boxSizing: 'border-box',
              overflowY: 'auto'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#fff',
                borderRadius: window.innerWidth <= 480 ? '12px' : '16px',
                width: window.innerWidth <= 480 ? '95%' : '90%',
                maxWidth: '450px',
                maxHeight: window.innerWidth <= 480 ? '90vh' : '85vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                position: 'relative',
                margin: 'auto'
              }}
            >
              {/* Close Button */}
              <button
                onClick={closeMemberModal}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: window.innerWidth <= 480 ? '32px' : '36px',
                  height: window.innerWidth <= 480 ? '32px' : '36px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: 'white',
                  color: '#333',
                  fontSize: window.innerWidth <= 480 ? '18px' : '20px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                ×
              </button>

              {/* Profile Photo Section */}
              <div
                style={{
                  height: window.innerWidth <= 480 ? '160px' : '200px',
                  background: 'linear-gradient(135deg, #00A86B 0%, #1abc9c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: window.innerWidth <= 480 ? '12px 12px 0 0' : '16px 16px 0 0'
                }}
              >
                <div
                  style={{
                    width: window.innerWidth <= 480 ? '100px' : '130px',
                    height: window.innerWidth <= 480 ? '100px' : '130px',
                    borderRadius: '50%',
                    border: '4px solid white',
                    overflow: 'hidden',
                    backgroundColor: '#e0e0e0',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                  }}
                >
                  {selectedMember.profilePhoto ? (
                    <img
                      src={getImageUrl(selectedMember.profilePhoto, API_BASE_URL)}
                      alt={getFullName(selectedMember) || 'Member'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: window.innerWidth <= 480 ? '2.5rem' : '3.5rem',
                      color: '#999',
                      backgroundColor: '#f5f5f5'
                    }}>
                      👤
                    </div>
                  )}
                </div>
              </div>

              {/* Content Section */}
              <div style={{ padding: window.innerWidth <= 480 ? '1rem' : '1.5rem', textAlign: 'center' }}>
                <h2 style={{
                  margin: '0 0 0.5rem',
                  color: '#333',
                  fontSize: window.innerWidth <= 480 ? '1.1rem' : '1.4rem',
                  fontWeight: '600',
                  wordBreak: 'break-word'
                }}>
                  {getFullName(selectedMember) || selectedMember.membershipNumber || 'Unnamed member'}
                </h2>

                <span style={{
                  display: 'inline-block',
                  padding: window.innerWidth <= 480 ? '4px 12px' : '6px 16px',
                  backgroundColor: '#00A86B',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: window.innerWidth <= 480 ? '0.75rem' : '0.85rem',
                  fontWeight: '600',
                  marginBottom: '1rem'
                }}>
                  {selectedMember.membershipNumber}
                </span>

                {/* Details */}
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: window.innerWidth <= 480 ? '0.75rem' : '1rem',
                  borderRadius: '10px',
                  textAlign: 'left'
                }}>
                  {selectedMember.areaOfSpecialty && (
                    <div style={{ marginBottom: '0.6rem' }}>
                      <div style={{ fontSize: window.innerWidth <= 480 ? '0.65rem' : '0.7rem', color: '#888', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>Specialty</div>
                      <div style={{ color: '#333', fontWeight: '500', fontSize: window.innerWidth <= 480 ? '0.85rem' : '1rem' }}>{selectedMember.areaOfSpecialty}</div>
                    </div>
                  )}

                  {selectedMember.currentPractice && (
                    <div style={{ marginBottom: '0.6rem' }}>
                      <div style={{ fontSize: window.innerWidth <= 480 ? '0.65rem' : '0.7rem', color: '#888', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>Current Practice</div>
                      <div style={{ color: '#333', fontWeight: '500', fontSize: window.innerWidth <= 480 ? '0.85rem' : '1rem' }}>{selectedMember.currentPractice}</div>
                    </div>
                  )}

                  {selectedMember.fellowshipCollege && (
                    <div style={{ marginBottom: '0.6rem' }}>
                      <div style={{ fontSize: window.innerWidth <= 480 ? '0.65rem' : '0.7rem', color: '#888', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>Fellowship</div>
                      <div style={{ color: '#333', fontWeight: '500', fontSize: window.innerWidth <= 480 ? '0.85rem' : '1rem' }}>{selectedMember.fellowshipCollege}</div>
                    </div>
                  )}

                  {selectedMember.joinedDate && (
                    <div>
                      <div style={{ fontSize: window.innerWidth <= 480 ? '0.65rem' : '0.7rem', color: '#888', textTransform: 'uppercase', fontWeight: '600', marginBottom: '2px' }}>Member Since</div>
                      <div style={{ color: '#333', fontWeight: '500', fontSize: window.innerWidth <= 480 ? '0.85rem' : '1rem' }}>{new Date(selectedMember.joinedDate).getFullYear()}</div>
                    </div>
                  )}
                </div>

                {/* Qualifications */}
                {(selectedMember.fwacs || selectedMember.fmcs || selectedMember.facs || selectedMember.frcs) && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '0.65rem' : '0.7rem', color: '#888', textTransform: 'uppercase', fontWeight: '600', marginBottom: '6px' }}>Qualifications</div>
                    <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      {selectedMember.fwacs && <span style={{ padding: '3px 8px', backgroundColor: '#e3f2fd', color: '#1565c0', borderRadius: '4px', fontSize: window.innerWidth <= 480 ? '0.7rem' : '0.75rem', fontWeight: '600' }}>FWACS</span>}
                      {selectedMember.fmcs && <span style={{ padding: '3px 8px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', fontSize: window.innerWidth <= 480 ? '0.7rem' : '0.75rem', fontWeight: '600' }}>FMCS</span>}
                      {selectedMember.facs && <span style={{ padding: '3px 8px', backgroundColor: '#fff3e0', color: '#ef6c00', borderRadius: '4px', fontSize: window.innerWidth <= 480 ? '0.7rem' : '0.75rem', fontWeight: '600' }}>FACS</span>}
                      {selectedMember.frcs && <span style={{ padding: '3px 8px', backgroundColor: '#fce4ec', color: '#c2185b', borderRadius: '4px', fontSize: window.innerWidth <= 480 ? '0.7rem' : '0.75rem', fontWeight: '600' }}>FRCS</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Profile Photo Viewer Modal */}
        {showPhotoViewer && memberData?.profilePhoto && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              padding: '20px',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
            onClick={() => setShowPhotoViewer(false)}
          >
            {/* Close Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPhotoViewer(false);
              }}
              style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                width: '45px',
                height: '45px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '2px solid white',
                cursor: 'pointer',
                fontSize: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s',
                zIndex: 10000
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>

            {/* Photo Container */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '90vw',
                maxHeight: '85vh'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={getImageUrl(memberData.profilePhoto, API_BASE_URL)}
                alt="Profile"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)'
                }}
              />
              <p style={{
                color: 'white',
                marginTop: '1rem',
                fontSize: '1.1rem',
                textAlign: 'center'
              }}>
                {memberData?.firstName} {memberData?.lastName}
              </p>
              <p style={{
                color: 'rgba(255, 255, 255, 0.7)',
                marginTop: '0.25rem',
                fontSize: '0.9rem'
              }}>
                Click anywhere to close
              </p>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowPasswordModal(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '90%',
                padding: '30px',
                position: 'relative',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: 'var(--primary-green)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>

              <h3 style={{ color: 'var(--primary-green)', textAlign: 'center', marginBottom: '1.5rem' }}>
                Change Password
              </h3>

              <form onSubmit={handlePasswordChange}>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="currentPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    required
                    minLength="6"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="confirmNewPassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    required
                    minLength="6"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '1rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '1.1rem',
                    backgroundColor: 'var(--primary-green)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Change Password
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberDashboard;